@if (showHeader) {
    <app-page-header title="Ping"></app-page-header>
}

<div class="ping-layout">
    <!-- Test form -->
    <app-section-card title="Network Test" subtitle="Test reachability from the processor, with an optional TCP port scan" icon="pi pi-wifi">
        <div class="form-body">
            <div class="field">
                <label for="ping-host">Hostname / IP</label>
                <input pInputText id="ping-host" type="text" [ngModel]="host()" (ngModelChange)="host.set($event)" placeholder="192.168.1.100" [disabled]="running()" (keydown.enter)="run()" class="w-full" />
            </div>

            <div class="field-row">
                <div class="field">
                    <label for="ping-count">Packets</label>
                    <p-inputnumber inputId="ping-count" [fluid]="true" [ngModel]="count()" (ngModelChange)="count.set($event)" [min]="limits.minCount" [max]="maxCount()" [showButtons]="true" [disabled]="running()" class="w-full" />
                </div>
                <div class="field">
                    <label for="ping-timeout">Timeout (ms)</label>
                    <p-inputnumber inputId="ping-timeout" [fluid]="true" [ngModel]="timeoutMs()" (ngModelChange)="onTimeoutChange($event)" [min]="limits.minTimeoutMs" [max]="limits.maxTimeoutMs" [step]="100" [disabled]="running()" class="w-full" />
                </div>
            </div>

            <div class="field">
                <div class="field-label-row">
                    <label for="ping-ports">
                        TCP ports <span class="optional">optional</span>
                    </label>
                    <div class="field-label-actions">
                        <button
                            pButton
                            type="button"
                            severity="secondary"
                            text
                            size="small"
                            (click)="useCommonAvPorts()"
                            [disabled]="running()"
                            pTooltip="Fill in the usual AV and control ports"
                            tooltipPosition="top"><span class="p-button-label">Common AV</span></button>
                        @if (ports()) {
                            <button
                                pButton
                                type="button"
                                severity="secondary"
                                text
                                size="small"
                                (click)="clearPorts()"
                                [disabled]="running()"><span class="p-button-label">Clear</span></button>
                        }
                    </div>
                </div>
                <input
                    pInputText
                    id="ping-ports"
                    type="text"
                    [ngModel]="ports()"
                    (ngModelChange)="ports.set($event)"
                    placeholder="e.g. 443 or 22,80,443,8000-8010"
                    [disabled]="running()"
                    (keydown.enter)="run()"
                    class="w-full"
                />
                @if (portsError()) {
                    <small class="hint hint-error">{{ portsError() }}</small>
                } @else if (portCount()) {
                    <small class="hint">{{ portCount() }} port{{ portCount() === 1 ? '' : 's' }} to scan &middot; max {{ maxPorts() }}</small>
                } @else {
                    <small class="hint">Many devices block ICMP but still accept TCP &mdash; add ports to check those too.</small>
                }
            </div>

            @if (portCount()) {
                <div class="field">
                    <label for="ping-port-timeout">Per-port timeout (ms)</label>
                    <p-inputnumber
                        inputId="ping-port-timeout"
                        [fluid]="true"
                        [ngModel]="portTimeoutMs()"
                        (ngModelChange)="portTimeoutMs.set($event)"
                        [min]="limits.minPortTimeoutMs"
                        [max]="limits.maxPortTimeoutMs"
                        [step]="100"
                        [disabled]="running()"
                        class="w-full" />
                    <small class="hint">A longer wait finds slow hosts but allows fewer ports.</small>
                </div>
            }

            <div class="field field-inline">
                <p-checkbox inputId="ping-dns" [binary]="true" [ngModel]="resolveDns()" (ngModelChange)="resolveDns.set($event)" [disabled]="running()" />
                <label for="ping-dns" class="inline-label">Resolve DNS (forward and reverse)</label>
            </div>

            <div class="form-actions">
                <button pButton type="button" (click)="run()" [disabled]="!canRun()" [loading]="running()">@if (running()) {<i class="pi pi-spinner pi-spin p-button-loading-icon" aria-hidden="true"></i>} @else {<i class="pi pi-play p-button-icon" aria-hidden="true"></i>}<span class="p-button-label">{{ running() ? 'Testing...' : 'Ping' }}</span></button>
            </div>

            @if (errorMessage()) {
                <div class="error-message"><i class="pi pi-exclamation-triangle"></i> {{ errorMessage() }}</div>
            }
        </div>
    </app-section-card>

    <!-- Result -->
    @if (result(); as res) {
        <app-section-card
            class="result-card"
            [title]="res.host"
            [subtitle]="summaryText() || null"
            [icon]="res.success ? 'pi pi-check-circle' : 'pi pi-times-circle'"
            [tone]="res.success ? 'success' : 'danger'"
        >
            <p-tag badge [severity]="res.success ? 'success' : 'danger'" [value]="res.success ? 'Reachable' : 'Unreachable'"></p-tag>
            @if (res.timedOut) {
                <p-tag badge severity="warn" value="Partial" pTooltip="The device's time budget expired; results are incomplete."></p-tag>
            }
            <div actions class="header-actions">
                <button
                    pButton
                    type="button"
                    severity="secondary"
                    outlined
                    size="small"
                    (click)="toggleConsole()"><i [class]="consoleExpanded() ? 'pi pi-chevron-up' : 'pi pi-chevron-down'" class="p-button-icon" aria-hidden="true"></i><span class="p-button-label">{{ consoleExpanded() ? 'Hide output' : 'Show output' }}</span></button>
            </div>

            @if (res.ports && res.ports.scanned > 1) {
                <div class="scan-summary">
                    @if (openPorts().length) {
                        <table class="port-table">
                            <thead>
                                <tr>
                                    <th>Port</th>
                                    <th>Service</th>
                                    <th class="numeric">Connect</th>
                                </tr>
                            </thead>
                            <tbody>
                                @for (probe of openPorts(); track probe.port) {
                                    <tr>
                                        <td class="port-number">{{ probe.port }}</td>
                                        <td class="port-service">{{ serviceFor(probe.port) || '—' }}</td>
                                        <td class="numeric">{{ probe.elapsedMs }} ms</td>
                                    </tr>
                                }
                            </tbody>
                        </table>
                    } @else {
                        <p class="no-open-ports">
                            No open ports among the {{ res.ports.scanned }} scanned.
                        </p>
                    }
                </div>
            }

            @if (consoleExpanded()) {
                <pre class="console-output">{{ consoleText() }}</pre>
            }
        </app-section-card>
    } @else {
        <app-section-card title="Results" icon="pi pi-wifi" tone="neutral">
            <app-empty-state icon="pi pi-wifi" title="No test run yet" description="Run a test to see results here." />
        </app-section-card>
    }
</div>
